import SiteCoreLinks from "@/components/SiteCoreLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { consolidateGraph } from "@/lib/schema-graph";
import { BookOpen, Clock, ChevronRight } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema } from "@/lib/aeo";
import EducationalTripleKeyLinks from "@/components/EducationalTripleKeyLinks";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/guides`;
const TITLE = "Ultimate Guides — คู่มือฉบับสมบูรณ์ Notary · NAATI · MFA · Visa | NYC Language Institute";
const DESC = "รวมคู่มือฉบับสมบูรณ์ 4 หัวข้อหลัก ครอบคลุมทุกประเด็นด้านกฎหมายและเอกสารระหว่างประเทศ — Notary Public, NAATI, MFA Legalization — คุยกับผู้เชี่ยวชาญตัวจริง ตอบกลับภายในไม่กี่นาที";

type GuideCard = { slug: string; h1: string; metaDesc: string; category: string; readMinutes: number; updated: string; words: number };

export const Route = createFileRoute("/guides")({
  loader: async () => {
    const { GUIDES, wordCount } = await import("@/data/guides");
    return {
      guides: GUIDES.map((g) => ({
        slug: g.slug,
        h1: g.h1,
        metaDesc: g.metaDesc,
        category: g.category,
        readMinutes: g.readMinutes,
        updated: g.updated,
        words: wordCount(g),
      })) as GuideCard[],
    };
  },
  head: ({ loaderData }: any) => ({
    meta: [
      { title: ctaTitle(TITLE, "/guides", "th")},
      { name: "description", content: ctaDesc(DESC, "/guides", "th")},
      { property: "og:title", content: ctaTitle(TITLE, "/guides", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/guides", "th")},
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks("/guides")],
    scripts: [consolidateGraph([
      speakableSchema([".aeo-speakable", "h1", "h2"]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": URL,
          name: TITLE,
          description: DESC,
          hasPart: ((loaderData?.guides ?? []) as GuideCard[]).map((g) => ({
            "@type": "Article",
            headline: g.h1,
            url: `${SITE}/guide/${g.slug}`,
            description: g.metaDesc,
            wordCount: g.words,
            datePublished: g.updated,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Ultimate Guides",
          itemListElement: ((loaderData?.guides ?? []) as GuideCard[]).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.h1,
            url: `${SITE}/guide/${g.slug}`,
          })),
        }),
      },
    ])],
  }),
  component: GuidesIndex,
});

function GuidesIndex() {
  const { guides } = Route.useLoaderData();
  return (
    <main className="min-h-screen bg-gradient-to-b from-navy-deep via-navy to-navy-deep text-white">
      <section className="border-b border-white/10 px-4 py-12 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-5xl space-y-4">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gold">
            <BookOpen className="h-3 w-3" /> Ultimate Guides · Pillar Content
          </div>
          <h1 className="text-3xl font-extrabold sm:text-5xl">คู่มือฉบับสมบูรณ์ 4 หัวข้อหลัก</h1>
          <p className="aeo-speakable max-w-3xl text-base text-white/80 sm:text-lg">
            ทีมทนายและที่ปรึกษาของ NYC รวบรวมความรู้ครบทุกประเด็นด้านเอกสาร กฎหมาย และวีซ่าระหว่างประเทศ — อ้างอิงกฎหมายไทยและข้อบังคับสากลล่าสุดปี 2025
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <div className="grid gap-6 md:grid-cols-2">
          {(guides as GuideCard[]).map((g) => (
            <Link
              key={g.slug}
              to="/guide/$slug"
              params={{ slug: g.slug }}
              className="group block rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:border-gold/40 hover:bg-white/[0.08]"
            >
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gold/80">
                <span>{g.category}</span>
                <span className="text-white/30">·</span>
                <span className="inline-flex items-center gap-1 text-white/80">
                  <Clock className="h-3 w-3" /> {g.readMinutes} นาที · {g.words.toLocaleString()} คำ
                </span>
              </div>
              <h2 className="mt-2 text-xl font-bold text-white group-hover:text-gold">{g.h1}</h2>
              <p className="mt-2 text-sm text-white/75">{g.metaDesc}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-gold">
                อ่านคู่มือ <ChevronRight className="h-4 w-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-white px-4 py-12 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <EducationalTripleKeyLinks mode="th" headingTh="พร้อมแปลเอกสารใช้จริง? เลือกเอกสาร + ฮับที่ใกล้คุณ" />
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}
